import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, Truck, Pencil, Trash2, X, Save } from 'lucide-react';
import { getTrasportatore, updateTrasportatore, deleteTrasportatore } from '../api/trasportatori';
import type { Trasportatore } from '../types/trasportatore';
import type { TrasportatoreUpdatePayload } from '../api/trasportatori';
import { trasportatoreUpdateSchema, type TrasportatoreUpdateData } from '../schemas/trasportatore';
import { listOmologhe } from '../api/omologhe';
import type { Omologa } from '../types/omologa';
import { useAuthStore } from '../store/authStore';
import { Skeleton, SkeletonRow } from '../components/ui/Skeleton';
import Badge from '../components/ui/Badge';
import { ANAGRAFICHE_TECNICHE_WRITE_ROLES } from '../lib/constants';

function formatDateIT(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' });
}

const inputCls = 'w-full text-sm text-ink-dark border border-stone-border rounded px-2 py-1.5 focus:outline-none focus:border-sage focus:ring-1 focus:ring-sage/20 bg-white';

function InfoRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-4 py-2.5 border-b border-stone-mid last:border-0 items-start">
      <dt className="text-xs text-ink-soft min-w-[160px] pt-0.5 flex-shrink-0">{label}</dt>
      <dd className="flex-1 text-sm text-ink-dark">{children}</dd>
    </div>
  );
}

function FieldWrap({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-4 py-1.5 border-b border-stone-mid last:border-0 items-start">
      <label className="text-xs text-ink-soft min-w-[160px] pt-2 flex-shrink-0">{label}</label>
      <div className="flex-1">
        {children}
        {error && <p className="text-xs text-accent-danger mt-0.5">{error}</p>}
      </div>
    </div>
  );
}

function PageSkeleton() {
  return (
    <div className="p-8 max-w-5xl mx-auto animate-pulse">
      <Skeleton className="h-4 w-32 mb-6" />
      <div className="bg-white border border-stone-mid rounded-xl overflow-hidden">
        <div className="px-5 py-3.5 border-b border-stone-mid bg-stone-bg"><Skeleton className="h-4 w-32" /></div>
        <div className="px-5 py-4 space-y-3">{[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-4 w-full" />)}</div>
      </div>
    </div>
  );
}

function stateBadgeVariant(stato: string): 'success' | 'danger' | 'neutral' {
  if (stato === 'valida') return 'success';
  if (stato === 'scaduta') return 'danger';
  return 'neutral';
}

export default function DettaglioTrasportatorePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const canEdit = (ANAGRAFICHE_TECNICHE_WRITE_ROLES as string[]).includes(user?.role ?? '');

  const [trasp, setTrasp] = useState<Trasportatore | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const [omologhe, setOmologhe] = useState<Omologa[]>([]);
  const [omologheLoading, setOmologheLoading] = useState(true);

  const { register, handleSubmit, reset, formState: { errors } } = useForm<TrasportatoreUpdateData>({
    resolver: zodResolver(trasportatoreUpdateSchema),
  });

  useEffect(() => {
    if (!id) return;
    void (async () => {
      setLoading(true);
      try {
        setTrasp(await getTrasportatore(id));
      } catch (err) {
        if ((err as { response?: { status?: number } })?.response?.status === 404) setNotFound(true);
        else setFetchError(t('anagrafiche.dett_cliente.errore_caricamento'));
      } finally {
        setLoading(false);
      }
    })();
  }, [id, t]);

  // Carica omologhe che usano questa anagrafica (filtro client-side sui ponti)
  useEffect(() => {
    if (!id) return;
    void (async () => {
      setOmologheLoading(true);
      try {
        const res = await listOmologhe({ limit: 100 });
        setOmologhe(res.items.filter((o) => o.trasportatori.some((tr) => tr.id === id)));
      } catch {
        setOmologhe([]);
      } finally {
        setOmologheLoading(false);
      }
    })();
  }, [id]);

  function startEdit() {
    if (!trasp) return;
    reset({
      ragione_sociale: trasp.ragione_sociale,
      numero_iscrizione_anga: trasp.numero_iscrizione_anga,
      data_rilascio: trasp.data_rilascio,
      categorie_classe: trasp.categorie_classe,
      piva: trasp.piva ?? '',
      referente: trasp.referente ?? '',
      contatti: trasp.contatti ?? '',
    });
    setIsEditing(true);
    setSaveError(null);
  }

  function buildPayload(d: TrasportatoreUpdateData): TrasportatoreUpdatePayload {
    return {
      ragione_sociale: d.ragione_sociale,
      numero_iscrizione_anga: d.numero_iscrizione_anga,
      data_rilascio: d.data_rilascio,
      categorie_classe: d.categorie_classe,
      piva: d.piva?.trim() || null,
      referente: d.referente?.trim() || null,
      contatti: d.contatti?.trim() || null,
    };
  }

  const onSubmit = async (data: TrasportatoreUpdateData) => {
    if (!id) return;
    setIsSaving(true);
    setSaveError(null);
    try {
      const updated = await updateTrasportatore(id, buildPayload(data));
      setTrasp(updated);
      setIsEditing(false);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 409) setSaveError(t('anagrafiche.crea_trasportatore.piva_esistente'));
      else setSaveError(t('anagrafiche.crea_trasportatore.errore_salvataggio'));
    } finally {
      setIsSaving(false);
    }
  };

  async function handleDelete() {
    if (!id) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await deleteTrasportatore(id);
      navigate('/trasportatori');
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number; data?: { detail?: string } } })?.response?.status;
      if (status === 409) {
        const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
        setDeleteError(detail ?? t('anagrafiche.dett_trasportatore.in_uso'));
      } else {
        setDeleteError(t('anagrafiche.dett_cliente.errore_cancellazione'));
      }
      setShowDeleteConfirm(false);
    } finally {
      setIsDeleting(false);
    }
  }

  if (loading) return <PageSkeleton />;
  if (notFound) {
    return (
      <div className="p-8 text-center">
        <p className="text-2xl font-bold text-ink-dark mb-2">404</p>
        <p className="text-ink-soft mb-4">{t('anagrafiche.dett_trasportatore.non_trovato')}</p>
        <Link to="/trasportatori" className="text-sage underline text-sm">{t('anagrafiche.dett_trasportatore.torna')}</Link>
      </div>
    );
  }
  if (fetchError || !trasp) {
    return (
      <div className="p-8 text-center">
        <p className="text-accent-danger font-medium mb-4">{fetchError ?? t('anagrafiche.dett_cliente.errore_sconosciuto')}</p>
        <button type="button" onClick={() => navigate('/trasportatori')} className="text-sage underline text-sm">{t('anagrafiche.dett_trasportatore.torna')}</button>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center gap-2 text-sm mb-6">
          <Link to="/trasportatori" className="text-sage hover:underline flex items-center gap-1">
            <ArrowLeft size={14} />
            {t('anagrafiche.trasportatori.titolo')}
          </Link>
          <span className="text-ink-faint">/</span>
          <span className="font-semibold text-ink-dark">{trasp.ragione_sociale}</span>
        </div>

        <div className="flex gap-6 items-start">
          <div className="flex-[3] min-w-0 space-y-4">
            <div className="bg-white border border-stone-mid rounded-xl overflow-hidden">
              <div className="px-5 py-3.5 border-b border-stone-mid bg-stone-bg flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Truck size={16} className="text-ink-soft" />
                  <span className="text-sm font-semibold text-ink-dark">{t('anagrafiche.dett_trasportatore.dati')}</span>
                </div>
                {canEdit && !isEditing && (
                  <button type="button" onClick={startEdit} data-testid="btn-modifica-trasportatore"
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-ink-mid border border-stone-border rounded-md hover:bg-white transition-colors">
                    <Pencil size={12} />{t('anagrafiche.dett_cliente.modifica')}
                  </button>
                )}
                {isEditing && (
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => setIsEditing(false)} disabled={isSaving}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-ink-mid border border-stone-border rounded-md hover:bg-white transition-colors">
                      <X size={12} />{t('common.azioni.annulla')}
                    </button>
                    <button type="button" onClick={handleSubmit(onSubmit)} disabled={isSaving}
                      data-testid="btn-salva-trasportatore"
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-sage rounded-md hover:bg-sage-dark transition-colors disabled:opacity-60">
                      <Save size={12} />{isSaving ? t('anagrafiche.dett_cliente.salvataggio') : t('anagrafiche.dett_cliente.salva')}
                    </button>
                  </div>
                )}
              </div>

              <div className="px-5 py-4">
                {saveError && (
                  <div className="mb-3 px-3 py-2 bg-accent-danger-bg border border-[#FECACA] rounded-md text-sm text-accent-danger">{saveError}</div>
                )}

                {isEditing ? (
                  <dl className="divide-y divide-stone-mid">
                    <FieldWrap label={t('anagrafiche.campi.ragione_sociale')} error={errors.ragione_sociale?.message}>
                      <input className={inputCls} {...register('ragione_sociale')} />
                    </FieldWrap>
                    <FieldWrap label={t('anagrafiche.campi.numero_anga')} error={errors.numero_iscrizione_anga?.message}>
                      <input className={inputCls} {...register('numero_iscrizione_anga')} />
                    </FieldWrap>
                    <FieldWrap label={t('anagrafiche.campi.data_rilascio')} error={errors.data_rilascio?.message}>
                      <input className={inputCls} type="date" {...register('data_rilascio')} />
                    </FieldWrap>
                    <FieldWrap label={t('anagrafiche.campi.categorie_classe')} error={errors.categorie_classe?.message}>
                      <input className={inputCls} {...register('categorie_classe')} />
                    </FieldWrap>
                    <FieldWrap label={t('anagrafiche.campi.partita_iva')} error={errors.piva?.message}>
                      <input className={inputCls} {...register('piva')} maxLength={11} placeholder={t('anagrafiche.crea_cliente.ph_piva')} />
                    </FieldWrap>
                    <FieldWrap label={t('anagrafiche.campi.referente')} error={errors.referente?.message}>
                      <input className={inputCls} {...register('referente')} />
                    </FieldWrap>
                    <FieldWrap label={t('anagrafiche.campi.contatti')} error={errors.contatti?.message}>
                      <input className={inputCls} {...register('contatti')} />
                    </FieldWrap>
                  </dl>
                ) : (
                  <dl className="divide-y divide-stone-mid">
                    <InfoRow label={t('anagrafiche.campi.ragione_sociale')}><span className="font-medium">{trasp.ragione_sociale}</span></InfoRow>
                    <InfoRow label={t('anagrafiche.campi.numero_anga')}><span className="font-mono">{trasp.numero_iscrizione_anga}</span></InfoRow>
                    <InfoRow label={t('anagrafiche.campi.data_rilascio')}>{formatDateIT(trasp.data_rilascio)}</InfoRow>
                    <InfoRow label={t('anagrafiche.campi.categorie_classe')}>{trasp.categorie_classe}</InfoRow>
                    <InfoRow label={t('anagrafiche.campi.partita_iva')}>{trasp.piva ? <span className="font-mono">{trasp.piva}</span> : <span className="text-ink-faint">—</span>}</InfoRow>
                    <InfoRow label={t('anagrafiche.campi.referente')}>{trasp.referente || <span className="text-ink-faint">—</span>}</InfoRow>
                    <InfoRow label={t('anagrafiche.campi.contatti')}>{trasp.contatti || <span className="text-ink-faint">—</span>}</InfoRow>
                    <InfoRow label={t('anagrafiche.dett_cliente.aggiornato_il')}><span className="text-ink-soft">{formatDateIT(trasp.modificato_at ?? trasp.created_at)}</span></InfoRow>
                  </dl>
                )}
              </div>
            </div>

            {/* Omologhe che usano questo trasportatore */}
            <div className="bg-white border border-stone-mid rounded-xl overflow-hidden">
              <div className="px-5 py-3.5 border-b border-stone-mid bg-stone-bg">
                <span className="text-sm font-semibold text-ink-dark">
                  {t('anagrafiche.dett_trasportatore.omologhe_anagrafica')} {!omologheLoading && `(${omologhe.length})`}
                </span>
              </div>
              <div className="divide-y divide-stone-mid">
                {omologheLoading ? (
                  <div className="px-5 py-3 space-y-2">{[1, 2].map((i) => <SkeletonRow key={i} cols={3} />)}</div>
                ) : omologhe.length === 0 ? (
                  <div className="px-5 py-4 text-sm text-ink-soft italic">{t('anagrafiche.dett_trasportatore.nessuna_omologa')}</div>
                ) : (
                  omologhe.map((o) => (
                    <div key={o.id}
                      onClick={() => navigate(`/produttori/${o.produttore_id}`)}
                      className="flex items-center justify-between px-5 py-3 hover:bg-stone-bg transition-colors cursor-pointer">
                      <div>
                        <p className="text-sm font-medium text-ink-dark">{o.numero_omologa ?? t('anagrafiche.dett_produttore.omologa_sn')}</p>
                        <p className="text-xs text-ink-soft">
                          {t('anagrafiche.dett_produttore.firma_scad', { firma: formatDateIT(o.data_firma), scad: formatDateIT(o.data_scadenza) })}
                        </p>
                      </div>
                      <Badge variant={stateBadgeVariant(o.stato)}>{o.stato}</Badge>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          <div className="flex-[1] min-w-[200px] space-y-3">
            {canEdit && !isEditing && (
              <button type="button" onClick={startEdit}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-sage border border-sage rounded-lg hover:bg-sage-light transition-colors">
                <Pencil size={14} />{t('anagrafiche.dett_cliente.modifica')}
              </button>
            )}
            {canEdit && (
              <>
                {!showDeleteConfirm ? (
                  <button type="button" onClick={() => { setDeleteError(null); setShowDeleteConfirm(true); }} disabled={isEditing}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-accent-danger border border-[#FECACA] rounded-lg hover:bg-accent-danger-bg transition-colors disabled:opacity-40">
                    <Trash2 size={14} />{t('anagrafiche.dett_cliente.elimina')}
                  </button>
                ) : (
                  <div className="border border-[#FECACA] rounded-lg p-4 bg-accent-danger-bg">
                    <p className="text-sm text-ink-dark font-medium mb-1">{t('anagrafiche.dett_cliente.conferma_elimina')}</p>
                    <p className="text-xs text-ink-soft mb-3">{t('anagrafiche.dett_cliente.conferma_elimina_nota')}</p>
                    {deleteError && <p className="text-xs text-accent-danger mb-2">{deleteError}</p>}
                    <div className="flex gap-2">
                      <button type="button" onClick={handleDelete} disabled={isDeleting}
                        className="flex-1 px-3 py-2 text-xs font-medium text-white bg-accent-danger rounded-md hover:bg-[#B91C1C] transition-colors disabled:opacity-60">
                        {isDeleting ? t('anagrafiche.dett_cliente.eliminazione') : t('anagrafiche.dett_cliente.si_elimina')}
                      </button>
                      <button type="button" onClick={() => setShowDeleteConfirm(false)} disabled={isDeleting}
                        className="flex-1 px-3 py-2 text-xs font-medium text-ink-mid border border-stone-border rounded-md hover:bg-white transition-colors">
                        {t('common.azioni.annulla')}
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
            <button type="button" onClick={() => navigate('/trasportatori')}
              className="w-full px-4 py-2 text-sm text-ink-soft border border-stone-border rounded-lg hover:bg-stone-bg transition-colors">
              {t('anagrafiche.dett_trasportatore.torna')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
